import dayjs from "dayjs";
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import FilterGroup from "../../common/reports/FilterGroup";
import FilterItem from "../../common/reports/FilterItem";
import Filters from "../../common/reports/Filters";
import {
  BETWEEN,
  DATE,
  GTE,
  LIKE,
  LTE,
  STRING,
} from "../../common/reports/reportConstants";
import { createFilterParam } from "../../common/reports/reportUtils";
import Button from "../../framework/Button";
import DatePicker from "../../framework/date/DatePicker";
import GlobalContext from "../../framework/GlobalContext";
import NumberInput from "../../framework/NumberInput";
import { executeScroll, isEmpty } from "../../utils/utils";
import Shortcut from "../../framework/Shortcut";
import SelectInput from "../../framework/SelectInput";
import { statusOptions } from "../../utils/constant";
import { fetchBranches } from "../../services/branch-service";
const StockRequestReportFilter = ({
  onFilterShow,
  applyFilterOnMount = true,
  reportRef,
}) => {
  const [startDate, setStartDate] = useState(dayjs().subtract(7, "day"));
  const [endDate, setEndDate] = useState(dayjs());
  const [branchSuggestions, setBranchSuggestions] = useState([]);
  const [selectedFromBranch, setSelectedFromBranch] = useState([]);
  const [selectedToBranch, setSelectedToBranch] = useState([]);
  const [status, setStatus] = useState("");
  const [limit, setLimit] = useState(100);
  const filterStartDateRef = useRef();
  const filterEndDateRef = useRef();
  const filterFromBranchIdRef = useRef();
  const filterToBranchIdRef = useRef();
  const filterStatusRef = useRef();
  const filterLimitRef = useRef();
  const [orderBy] = useState([{ fieldName: "updatedTime", orderType: "desc" }]);

  const { setCurrentComponent } = useContext(GlobalContext);

  const handleFetchBranches = useCallback((event) => {
    setBranchSuggestions([]);
    fetchBranches(`deleted=false`).then((resp) => {
      let updatedResp = resp.result.rows.map((branch) => ({
        id: branch._id,
        label: branch.code,
      }));
      setBranchSuggestions(updatedResp);
    });
  }, []);

  const applyFilterHandler = useCallback(
    (isFilterChanged = false) => {
      let filters = [];
      let result = {};
      const _startDate = startDate?.format("DD/MM/YYYY");
      const _endDate = endDate?.format("DD/MM/YYYY");

      if (_startDate && !_endDate) {
        filters.push(createFilterParam("updatedTime", _startDate, DATE, GTE));
      }
      if (!_startDate && _endDate) {
        filters.push(createFilterParam("updatedTime", _endDate, DATE, LTE));
      }
      if (_startDate && _endDate) {
        filters.push(
          createFilterParam(
            "updatedTime",
            { start: _startDate, end: _endDate },
            DATE,
            BETWEEN
          )
        );
      }
      if (!isEmpty(selectedFromBranch)) {
        filters.push(
          createFilterParam(
            "fromBranchId",
            selectedFromBranch?.label,
            STRING,
            LIKE
          )
        );
      }
      if (!isEmpty(selectedToBranch)) {
        filters.push(
          createFilterParam("toBranchId", selectedToBranch?.label, STRING, LIKE)
        );
      }
      if (!isEmpty(status)) {
        filters.push(createFilterParam("status", status, "string", LIKE));
      }
      result.filters = filters;
      result.isFilterChanged = isFilterChanged;

      if (orderBy) {
        result.orderBy = orderBy;
      }
      if (!isEmpty(limit)) {
        result.limit = limit;
      }

      reportRef.current?.applyFilters(result);
    },
    [
      selectedFromBranch,
      selectedToBranch,
      endDate,
      limit,
      orderBy,
      reportRef,
      startDate,
      status,
    ]
  );

  const filterListener = useCallback(
    (event) => {
      const key = event.key.toLowerCase();
      const ctrl = event.ctrlKey;
      if (ctrl && key === "enter") {
        applyFilterHandler(true);
        return;
      }
    },
    [applyFilterHandler]
  );
  const filterContent = (
    <>
      <FilterGroup>
        <FilterItem>
          <DatePicker
            id="srr_filter_start_date"
            label="Start Date"
            inputRef={filterStartDateRef}
            onChange={(value) => setStartDate(value)}
            onFocus={() => setCurrentComponent("srr_filter_start_date")}
            value={startDate}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "srr_filter_start_date",
                down: "srr_filter_end_date",
                left: "srr_filter_start_date",
                right: "srr_filter_end_date",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <DatePicker
            id="srr_filter_end_date"
            label="End Date"
            inputRef={filterEndDateRef}
            onChange={(value) => setEndDate(value)}
            onFocus={() => {
              executeScroll(filterEndDateRef);
              setCurrentComponent("srr_filter_end_date");
            }}
            value={endDate}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "srr_filter_start_date",
                down: "srr_filter_from_branch",
                left: "srr_filter_start_date",
                right: "srr_filter_from_branch",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="srr_filter_from_branch"
            label="From Branch"
            value={selectedFromBranch}
            ref={filterFromBranchIdRef}
            options={branchSuggestions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => {
              return option.label === value.label;
            }}
            onChange={(e, value) => {
              setSelectedFromBranch(value);
            }}
            onFocus={() => {
              executeScroll(filterFromBranchIdRef);
              setCurrentComponent("srr_filter_from_branch");
            }}
            disableClearable={false}
            markers={JSON.stringify({
              up: "srr_filter_end_date",
              down: "srr_filter_to_branch",
              left: "srr_filter_end_date",
              right: "srr_filter_to_branch",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="srr_filter_to_branch"
            label="To Branch"
            value={selectedToBranch}
            ref={filterToBranchIdRef}
            options={branchSuggestions}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => {
              return option.label === value.label;
            }}
            onChange={(e, value) => {
              setSelectedToBranch(value);
            }}
            onFocus={() => {
              executeScroll(filterToBranchIdRef);
              setCurrentComponent("srr_filter_to_branch");
            }}
            disableClearable={false}
            markers={JSON.stringify({
              up: "srr_filter_from_branch",
              down: "srr_filter_status",
              left: "srr_filter_from_branch",
              right: "srr_filter_status",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <SelectInput
            id="srr_filter_status"
            label="Status"
            ref={filterStatusRef}
            options={statusOptions.map((status) => ({
              value: status,
              label: status,
            }))}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.label
            }
            value={status}
            onChange={(e, option) => setStatus(option?.value || "")}
            onFocus={() => {
              executeScroll(filterStatusRef);
              setCurrentComponent("srr_filter_status");
            }}
            disableClearable={false}
            markers={JSON.stringify({
              up: "srr_filter_to_branch",
              down: "srr_filter_limit",
              left: "srr_filter_to_branch",
              right: "srr_filter_limit",
            })}
          />
        </FilterItem>
      </FilterGroup>
      <FilterGroup>
        <FilterItem>
          <NumberInput
            id="srr_filter_limit"
            label="Limit"
            ref={filterLimitRef}
            onValueChange={(values) => setLimit(values.floatValue)}
            isAllowed={(values) => values?.formattedValue <= 999999}
            onFocus={() => {
              executeScroll(filterLimitRef);
              setCurrentComponent("srr_filter_limit");
            }}
            value={limit}
            inputProps={{
              "data-markers": JSON.stringify({
                up: "srr_filter_status",
                down: "srr_apply_filter_button",
                left: "srr_filter_status",
                right: "srr_apply_filter_button",
              }),
            }}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );

  const filterFooter = (
    <>
      <FilterGroup>
        <FilterItem>
          <Button
            id="srr_apply_filter_button"
            label="Apply"
            endIcon={<Shortcut keys={["Ctrl", "Enter"]} />}
            onClick={() => applyFilterHandler(true)}
            markers={JSON.stringify({
              up: "srr_filter_limit",
              down: "srr_apply_filter_button",
              left: "srr_filter_limit",
              right: "srr_apply_filter_button",
            })}
          />
        </FilterItem>
      </FilterGroup>
    </>
  );
  useEffect(() => {
    handleFetchBranches();
  }, [handleFetchBranches]);
  useEffect(() => {
    if (applyFilterOnMount) applyFilterHandler();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyFilterOnMount]);

  useEffect(() => {
    if (onFilterShow) {
      filterStartDateRef?.current?.focus({ preventScroll: true });
    }
  }, [onFilterShow]);

  useEffect(() => {
    if (onFilterShow) {
      document.addEventListener("keyup", filterListener);
    }
    return () => {
      document.removeEventListener("keyup", filterListener);
    };
  }, [filterListener, onFilterShow]);

  return (
    <Filters
      title="Stock Request Report Filters"
      filterContent={filterContent}
      filterFooter={filterFooter}
    />
  );
};

export default StockRequestReportFilter;
